import { API_URL } from '@/config/api';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useUserStore } from '@/store/useUserStore';
import { useBillingRequiredStore } from '@/store/useBillingRequiredStore';

export class HttpError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}

interface FetchOptions extends RequestInit {
  baseURL?: string;
  skipAuth?: boolean;
}

export async function fetchClient<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { baseURL = API_URL, headers = {}, skipAuth = false, ...rest } = options;

  // Get token from user store (unless skipAuth is true)
  const { session } = useUserStore.getState();
  const token = !skipAuth ? session?.access_token : null;

  // Get selected organization ID (unless skipAuth is true)
  const { selectedOrganizationId } = useOrganizationStore.getState();

  const response = await fetch(`${baseURL}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(selectedOrganizationId && !skipAuth && {
        'X-Organization-ID': selectedOrganizationId,
      }),
      ...headers,
    },
    ...rest,
  });

  if (!response.ok) {
    // Try to get error details from response
    try {
      const errorData = await response.json();
      if (response.status === 402) {
        const billingMessage =
          typeof errorData === 'object' && errorData !== null
            ? errorData.message || errorData.error
            : undefined;
        useBillingRequiredStore.getState().open(billingMessage);
      }

      if (response.status === 401) {
        throw new HttpError(
          'Unauthorized: Please check your authentication token',
          response.status,
          errorData
        );
      }
      // Use error message from API if available, otherwise fallback to status text
      // Throw HttpError instead of standard Error
      throw new HttpError(
        errorData.message ||
          errorData.error ||
          `API request failed: ${response.statusText}`,
        response.status,
        errorData
      );
    } catch (e) {
      // If the error is already an HttpError (thrown in try block), re-throw it
      if (e instanceof HttpError) {
        throw e;
      }

      // If we can't parse the error response, throw a generic error with status
      if (response.status === 402) {
        useBillingRequiredStore
          .getState()
          .open('Your organization must update billing to continue.');
      }

      if (response.status === 401) {
        throw new HttpError(
          'Unauthorized: Please check your authentication token',
          response.status
        );
      }
      // Throw HttpError with status and statusText
      throw new HttpError(
        `API request failed with status ${response.status}: ${response.statusText}`,
        response.status
      );
    }
  }

  // For 204 No Content responses, return undefined
  if (response.status === 204) {
    return undefined as T;
  }

  // For other successful responses, try to parse JSON
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (e) {
      throw new Error('Failed to parse API response');
    }
  }

  // If not JSON or no content-type header, return undefined
  return undefined as T;
}
